import React, { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Check, Printer, RotateCcw } from "lucide-react";
import { Pill, Reveal, useInView } from "./Reveal";
import { PillButton } from "./Kit";
import { Tween } from "./Journey";
import ShaderBackground from "./ShaderBackground";
import Radar from "./Radar";
import {
  BANDS,
  CALENDAR_URL,
  MAX_SCORE,
  bandIndex,
  focusLine,
  gaps,
  letterOf,
  encode,
  notes,
  total,
  where,
} from "@/lib/scorecard";

const EASE = [0.15, 0.9, 0.34, 0.95];

function Gauge({ score, band }) {
  const reduce = useReducedMotion();
  const R = 100;
  const C = 2 * Math.PI * R;
  // Band thresholds sit at 8/21 and 15/21 of the ring.
  const tick = (value) => {
    const a = (value / MAX_SCORE) * 2 * Math.PI;
    return [120 + Math.cos(a) * (R + 12), 120 + Math.sin(a) * (R + 12)];
  };
  return (
    <div className="relative mx-auto h-[250px] w-[250px] sm:h-[290px] sm:w-[290px]">
      <svg
        viewBox="0 0 240 240"
        className="h-full w-full -rotate-90"
        aria-hidden="true"
      >
        <circle
          cx="120"
          cy="120"
          r={R}
          fill="none"
          stroke="rgba(255,255,255,.14)"
          strokeWidth="6"
        />
        <motion.circle
          cx="120"
          cy="120"
          r={R}
          fill="none"
          stroke="#fff"
          strokeWidth="6"
          strokeLinecap="round"
          strokeDasharray={C}
          initial={{ strokeDashoffset: C }}
          animate={{ strokeDashoffset: C * (1 - score / MAX_SCORE) }}
          transition={{ duration: reduce ? 0 : 1.4, ease: EASE, delay: 0.2 }}
        />
        {[8.5, 15.5].map((t) => {
          const [x, y] = tick(t);
          return (
            <circle key={t} cx={x} cy={y} r="2" fill="rgba(255,255,255,.5)" />
          );
        })}
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span
          className="nm-display text-[84px] font-medium leading-none tracking-[-0.03em] sm:text-[100px]"
          data-testid="scorecard-score"
        >
          <Tween value={score} duration={1400} />
        </span>
        <span className="mt-1 text-[13px] text-white/50">
          out of {MAX_SCORE}
        </span>
        <span className="sr-only">{BANDS[band].title}</span>
      </div>
    </div>
  );
}

function Bars({ answers, questions }) {
  const [ref, seen] = useInView(0.2);
  return (
    <div ref={ref} className="space-y-3">
      {questions.map((q, i) => (
        <div key={q.area} className="flex items-center gap-4">
          <span className="w-[112px] shrink-0 text-[14px] tracking-[-0.01em] sm:w-[140px]">
            {q.area}
          </span>
          <div className="flex flex-1 gap-1.5">
            {[0, 1, 2].map((s) => (
              <span
                key={s}
                className="h-[10px] flex-1 overflow-hidden rounded-full bg-black/[0.07]"
              >
                <span
                  className="block h-full origin-left rounded-full bg-black transition-transform duration-700"
                  style={{
                    transform: `scaleX(${seen && answers[i] > s ? 1 : 0})`,
                    transitionDelay: `${i * 70 + s * 110}ms`,
                    transitionTimingFunction: "cubic-bezier(.15,.9,.34,.95)",
                  }}
                />
              </span>
            ))}
          </div>
          <span className="w-8 text-right text-[12px] tabular-nums opacity-40">
            {answers[i]}/3
          </span>
        </div>
      ))}
    </div>
  );
}

function Plan({ steps }) {
  const [open, setOpen] = useState(0);
  const days = [30, 60, 90];
  return (
    <div className="grid gap-3 lg:grid-cols-12">
      <div className="flex gap-2 lg:col-span-4 lg:flex-col">
        {days.map((d, i) => (
          <button
            key={d}
            type="button"
            onClick={() => setOpen(i)}
            aria-pressed={open === i}
            className={`group flex flex-1 items-center justify-between rounded-[20px] px-5 py-5 text-left transition-colors duration-300 lg:flex-none lg:px-6 ${
              open === i
                ? "bg-black text-white"
                : "bg-[#f7f7f7] hover:bg-black/[0.07]"
            }`}
          >
            <span className="nm-display text-[34px] leading-none tracking-[-0.03em] sm:text-[44px]">
              {d}
            </span>
            <span className="hidden text-[12px] uppercase tracking-[0.1em] opacity-50 sm:inline">
              days
            </span>
          </button>
        ))}
      </div>
      <div className="relative min-h-[170px] rounded-[28px] bg-[#f7f7f7] p-7 sm:p-10 lg:col-span-8">
        <div className="mb-6 flex gap-1.5">
          {days.map((d, i) => (
            <span
              key={d}
              className={`h-[3px] flex-1 rounded-full transition-colors duration-500 ${i <= open ? "bg-black" : "bg-black/10"}`}
            />
          ))}
        </div>
        <motion.p
          key={open}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, ease: EASE }}
          className="nm-title text-[24px] leading-[1.15] tracking-[-0.02em] sm:text-[34px]"
        >
          {steps[open]}
        </motion.p>
      </div>
    </div>
  );
}

function Capture({ answers, band, focus, card }) {
  const focusLabel = card.focus.options.find((o) => o.id === focus)?.label;
  const [f, setF] = useState({ name: "", email: "" });
  const [state, setState] = useState("idle");
  const send = async (e) => {
    e.preventDefault();
    if (!f.name.trim() || !/^\S+@\S+\.\S+$/.test(f.email)) {
      setState("invalid");
      return;
    }
    setState("sending");
    try {
      const r = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: f.name,
          email: f.email,
          topics: ["Scorecard"],
          message: `Agent-readiness scorecard (${card.label.toLowerCase()}): ${total(answers)}/${MAX_SCORE} (${BANDS[band].short}). Answers ${letterOf(answers)}.${focusLabel ? ` Biggest time sink: ${focusLabel}.` : ""} Please send one thing to fix first.`,
          page: `${card.path}?sc=${encode(answers)}`,
        }),
      });
      setState(r.ok ? "sent" : "error");
    } catch {
      setState("error");
    }
  };
  if (state === "sent")
    return (
      <p
        role="status"
        data-testid="scorecard-capture-sent"
        className="flex items-center gap-3 rounded-[20px] bg-white/10 p-5 text-[16px]"
      >
        <Check size={18} /> Thanks. We'll reply within 24 hours with the one
        thing we'd fix first.
      </p>
    );
  return (
    <form
      onSubmit={send}
      noValidate
      className="space-y-3"
      data-testid="scorecard-capture"
    >
      <p className="text-[14px] text-white/60">
        Want a person to look at this result? Leave your email and we'll reply
        within 24 hours with the one thing we'd fix first.
      </p>
      <div className="grid gap-2 sm:grid-cols-2">
        <input
          aria-label="Your name"
          placeholder="Your name"
          autoComplete="name"
          value={f.name}
          onChange={(e) => setF({ ...f, name: e.target.value })}
          className="h-12 rounded-[14px] bg-white/10 px-4 text-[15px] text-white outline-none ring-1 ring-white/10 placeholder:text-white/35 focus:ring-white/50"
        />
        <input
          aria-label="Work email"
          placeholder="Work email"
          type="email"
          autoComplete="email"
          value={f.email}
          onChange={(e) => setF({ ...f, email: e.target.value })}
          className="h-12 rounded-[14px] bg-white/10 px-4 text-[15px] text-white outline-none ring-1 ring-white/10 placeholder:text-white/35 focus:ring-white/50"
        />
      </div>
      <div className="flex flex-wrap items-center gap-4">
        <PillButton type="submit" variant="inverse" size="lg">
          {state === "sending" ? "Sending…" : "Send my result"}
        </PillButton>
        {state === "invalid" && (
          <span role="alert" className="text-[13px] text-red-300">
            Add your name and a valid email.
          </span>
        )}
        {state === "error" && (
          <span role="alert" className="text-[13px] text-red-300">
            That did not go through. Email hello@nomus.work instead.
          </span>
        )}
      </div>
    </form>
  );
}

export default function ScorecardResult({ card, answers, focus, onRestart }) {
  const score = total(answers);
  const band = bandIndex(answers);
  const data = { ...BANDS[band], ...card.results[band] };
  const flagged = notes(answers, card);
  const weak = gaps(answers, card);
  const tailored = focusLine(answers, focus, card);
  const link = `/contact?sc=${encode(answers)}${card.category ? `&audience=${card.category}` : ""}`;
  return (
    <main className="bg-white text-black" data-testid="scorecard-result">
      <section className="relative overflow-hidden bg-black px-5 pb-16 pt-28 text-white sm:pt-32">
        <ShaderBackground dark strength={0.22} lines={12} fadeLeft={0} />
        <div className="relative z-10 mx-auto grid max-w-[1400px] items-center gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <Gauge score={score} band={band} />
            <div className="mt-8 grid grid-cols-3 gap-1.5">
              {BANDS.map((b, i) => (
                <div key={b.id}>
                  <div
                    className={`h-[3px] rounded-full ${i <= band ? "bg-white" : "bg-white/20"}`}
                  />
                  <p
                    className={`mt-2 text-[11px] ${i === band ? "font-medium" : "opacity-40"}`}
                  >
                    {b.short} · {b.range}
                  </p>
                </div>
              ))}
            </div>
          </div>
          <div className="lg:col-span-7">
            <Pill white>Your result</Pill>
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: EASE, delay: 0.3 }}
              className="nm-title mt-6 text-[40px] leading-[0.98] sm:text-[68px]"
              data-testid="scorecard-band"
            >
              {data.title}
            </motion.h1>
            <p className="mt-4 text-[16px] text-white/55">{data.meaning}</p>
            <p className="mt-8 max-w-[640px] text-[19px] leading-[1.35] tracking-[-0.01em] text-white/85 sm:text-[22px]">
              {where(answers, card)}
            </p>
            {(tailored || flagged.length > 0) && (
              <div className="mt-6 flex max-w-[640px] flex-col gap-2">
                {[...flagged, tailored].filter(Boolean).map((t) => (
                  <p
                    key={t}
                    className="rounded-[16px] bg-white/10 px-5 py-3 text-[14px] leading-[1.4] text-white/85"
                  >
                    {t}
                  </p>
                ))}
              </div>
            )}
            <div className="mt-8 flex flex-wrap items-center gap-3 print:hidden">
              <PillButton to={link} variant="inverse" size="lg">
                Talk this through
              </PillButton>
              <button
                type="button"
                onClick={() => window.print()}
                className="inline-flex h-11 items-center gap-2 rounded-full px-5 text-[13px] ring-1 ring-white/25 transition-colors hover:bg-white/10"
              >
                <Printer size={14} /> Save as PDF
              </button>
              <button
                type="button"
                onClick={onRestart}
                className="inline-flex h-11 items-center gap-2 px-3 text-[13px] text-white/55 transition-colors hover:text-white"
              >
                <RotateCcw size={14} /> Retake
              </button>
            </div>
          </div>
        </div>
      </section>

      <section className="px-5 pt-20 lg:pt-28">
        <div className="mx-auto grid max-w-[1400px] gap-10 lg:grid-cols-12">
          <Reveal className="lg:col-span-5">
            <Pill>Where your {card.noun} is today</Pill>
            <h2 className="nm-title mt-5 text-[34px] leading-[1.02] sm:text-[52px]">
              Area by area.
            </h2>
            <p className="mt-5 max-w-[420px] text-[18px] leading-[1.3] opacity-60">
              {weak.length
                ? `Biggest gaps: ${weak.map((w) => w.area).join(" and ")}.`
                : "No area scores below 2. Your foundations are solid."}
            </p>
            <div className="mt-8 hidden rounded-[28px] bg-[#f7f7f7] p-6 lg:block">
              <Radar
                values={answers}
                questions={card.questions}
                className="w-full text-black"
              />
            </div>
          </Reveal>
          <Reveal className="lg:col-span-7" delay={120}>
            <div className="rounded-[28px] bg-[#f7f7f7] p-6 sm:p-10">
              <Bars answers={answers} questions={card.questions} />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="px-5 pt-20 lg:pt-28">
        <div className="mx-auto max-w-[1400px]">
          <Reveal>
            <Pill>Three things to do first</Pill>
          </Reveal>
          <div className="mt-6 grid gap-3 md:grid-cols-3">
            {data.moves.map((m, i) => (
              <Reveal key={m} delay={i * 100}>
                <div className="group flex h-full min-h-[240px] flex-col justify-between rounded-[28px] bg-[#f7f7f7] p-7 transition-colors duration-500 hover:bg-black hover:text-white sm:p-9">
                  <span className="nm-display text-[56px] leading-none tracking-[-0.03em] opacity-25 transition-opacity group-hover:opacity-60">
                    0{i + 1}
                  </span>
                  <p className="mt-10 text-[20px] leading-[1.2] tracking-[-0.02em] sm:text-[24px]">
                    {m}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="px-5 pt-20 lg:pt-28">
        <div className="mx-auto max-w-[1400px]">
          <Reveal>
            <Pill>Your next 90 days</Pill>
            <h2 className="nm-title mb-8 mt-5 max-w-[760px] text-[34px] leading-[1.02] sm:text-[52px]">
              What the next quarter looks like.
            </h2>
          </Reveal>
          <Plan steps={data.plan} />
        </div>
      </section>

      <section className="px-3 pb-3 pt-20 sm:px-5 sm:pb-5 lg:pt-28 print:hidden">
        <div className="relative mx-auto max-w-[1400px] overflow-hidden rounded-[28px] bg-black p-7 text-white sm:p-12">
          <ShaderBackground dark strength={0.2} lines={12} fadeLeft={0} />
          <div className="relative z-10 grid gap-10 lg:grid-cols-12">
            <div className="lg:col-span-6">
              <Pill white>Want this mapped for your {card.noun}?</Pill>
              <h2 className="nm-title mt-6 text-[36px] leading-[1.02] sm:text-[56px]">
                Agent-Readiness Audit.
              </h2>
              <p className="mt-6 max-w-[520px] text-[18px] leading-[1.35] text-white/75">
                We map your systems, workflows and data access, then hand you a
                prioritized plan: which tool to connect first, what it would
                take, and what's safe.
              </p>
              <dl className="mt-8 grid max-w-[520px] grid-cols-3 gap-3 text-[13px]">
                {[
                  ["$850–1,700", "by team size"],
                  ["10–14 days", "to delivery"],
                  ["Credited", "toward your first build"],
                ].map(([k, v]) => (
                  <div key={k} className="rounded-[16px] bg-white/10 p-4">
                    <dt className="nm-display text-[19px] leading-tight tracking-[-0.02em] sm:text-[22px]">
                      {k}
                    </dt>
                    <dd className="mt-1 text-white/55">{v}</dd>
                  </div>
                ))}
              </dl>
              <div className="mt-8 flex flex-wrap gap-3">
                <PillButton to={link} variant="inverse" size="lg">
                  Book the audit
                </PillButton>
                {CALENDAR_URL ? (
                  <PillButton href={CALENDAR_URL} variant="white" size="lg">
                    20-minute fit call
                  </PillButton>
                ) : null}
              </div>
              <p className="mt-6 text-[12px] text-white/40">
                Answers stay in your browser unless you send them to us. No
                client data is collected.
              </p>
            </div>
            <div className="lg:col-span-5 lg:col-start-8">
              <Capture
                answers={answers}
                band={band}
                focus={focus}
                card={card}
              />
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
